/* ─── graphDraft ─────────────────────────────────────────────────────
   Modèle de brouillon pour l'édition depuis la vue graphe.

   Pourquoi un brouillon plutôt qu'une sauvegarde à chaque geste : un seul
   geste dans le graphe peut toucher TROIS collections différentes
   (`games_config.pile`, `decisions.add_elem_to_pile_*`,
   `pages.page_general_consequence`), et un état intermédiaire peut être
   temporairement invalide. On accumule donc les modifications, on les
   affiche, et on ne déclenche la validation pré-sauvegarde du serveur
   qu'une seule fois — ce qui évite aussi de redemander cinq fois de suite
   "cette modification affecte 4 ateliers".

   Toutes les fonctions sont pures : elles renvoient un nouveau brouillon,
   ce qui rend l'annulation (Cmd+Z) triviale côté composant.
*/

export const emptyDraft = {
  config: null,       // copie de travail de games_config (null = inchangé)
  pages: {},          // { [pageId]: page } — copies de travail modifiées
  decisions: {},      // { [decisionId]: decision }
  createdPages: [],   // ids des pages créées dans le brouillon
  changes: [],        // [{ id, label, target }] pour l'affichage
};

let changeSeq = 0;
const change = (label, target) => ({ id: `chg-${changeSeq++}`, label, target });

export function isDirty(draft) {
  return draft.changes.length > 0;
}

/* ─── Lecture de l'état effectif (base + brouillon) ─────────────── */

export function effectiveConfig(draft, baseConfig) {
  return draft.config || baseConfig;
}

export function effectivePages(draft, basePages) {
  const overridden = draft.pages;
  const merged = basePages.map((p) => overridden[p._id] || p);
  const baseIds = new Set(basePages.map((p) => p._id));
  Object.values(overridden).forEach((p) => { if (!baseIds.has(p._id)) merged.push(p); });
  return merged;
}

export function effectiveDecisions(draft, baseDecisions) {
  const overridden = draft.decisions;
  return baseDecisions.map((d) => overridden[d._id] || d);
}

/* ─── Helpers internes ──────────────────────────────────────────── */

const cloneConfig = (draft, baseConfig) =>
  JSON.parse(JSON.stringify(draft.config || baseConfig));

const cloneDecision = (draft, base, id) =>
  JSON.parse(JSON.stringify(draft.decisions[id] || base.decisionsById.get(id) || { _id: id }));

const clonePage = (draft, base, id) =>
  JSON.parse(JSON.stringify(draft.pages[id] || base.pagesById.get(id) || { _id: id }));

const withChange = (draft, patch, label, target) => ({
  ...draft,
  ...patch,
  changes: [...draft.changes, change(label, target)],
});

/* ─── Opérations sur la pile (games_config) ─────────────────────── */

export function reorderPile(draft, base, fromIndex, toIndex) {
  const config = cloneConfig(draft, base.config);
  const pile = [...(config.pile || [])];
  if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= pile.length) return draft;
  const [moved] = pile.splice(fromIndex, 1);
  pile.splice(Math.max(0, Math.min(toIndex, pile.length)), 0, moved);
  config.pile = pile;
  const label = `Pile : « ${base.pageLabel(moved)} » déplacée en position ${Math.min(toIndex, pile.length - 1) + 1}`;
  return withChange(draft, { config }, label, { type: "config" });
}

export function insertPileStep(draft, base, index, pageId) {
  const config = cloneConfig(draft, base.config);
  const pile = [...(config.pile || [])];
  pile.splice(Math.max(0, Math.min(index, pile.length)), 0, pageId);
  config.pile = pile;
  return withChange(
    draft, { config },
    `Pile : « ${base.pageLabel(pageId)} » ajoutée en position ${index + 1}`,
    { type: "config" }
  );
}

export function removePileStep(draft, base, index) {
  const config = cloneConfig(draft, base.config);
  const pile = [...(config.pile || [])];
  const [removed] = pile.splice(index, 1);
  if (removed === undefined) return draft;
  config.pile = pile;
  return withChange(
    draft, { config },
    `Pile : « ${base.pageLabel(removed)} » retirée`,
    { type: "config" }
  );
}

/* ─── Opérations sur les conséquences ───────────────────────────── */

const LIST_LABEL = {
  add_elem_to_pile_if_taken: "si prise",
  add_elem_to_pile_if_not_taken: "si non prise",
  page_general_consequence: "conséquence de page",
};

/**
 * Ajoute une conséquence.
 * ref : { doc: "decision"|"page", id, list }
 * Le champ écrit est `consequence_id` (générique). Si l'auteur veut une
 * variante par atelier, il passe par l'éditeur complet — le graphe reste
 * sur le cas simple pour ne pas enfouir de la spécificité d'atelier dans un
 * document partagé sans que ce soit un choix explicite.
 */
export function addConsequence(draft, base, ref, targetPageId) {
  const consequence = { condition: false, consequence_id: targetPageId };

  if (ref.doc === "decision") {
    const decision = cloneDecision(draft, base, ref.id);
    decision[ref.list] = [...(decision[ref.list] || []), consequence];
    return withChange(
      draft,
      { decisions: { ...draft.decisions, [ref.id]: decision } },
      `« ${base.decisionLabel(ref.id)} » (${LIST_LABEL[ref.list]}) → ajoute « ${base.pageLabel(targetPageId)} »`,
      { type: "decision", id: ref.id }
    );
  }

  const page = clonePage(draft, base, ref.id);
  page.page_general_consequence = [...(page.page_general_consequence || []), consequence];
  return withChange(
    draft,
    { pages: { ...draft.pages, [ref.id]: page } },
    `Page « ${base.pageLabel(ref.id)} » → ajoute « ${base.pageLabel(targetPageId)} »`,
    { type: "page", id: ref.id }
  );
}

export function removeConsequence(draft, base, ref) {
  if (ref.doc === "decision") {
    const decision = cloneDecision(draft, base, ref.id);
    const list = [...(decision[ref.list] || [])];
    const [removed] = list.splice(ref.index, 1);
    if (!removed) return draft;
    decision[ref.list] = list;
    return withChange(
      draft,
      { decisions: { ...draft.decisions, [ref.id]: decision } },
      `« ${base.decisionLabel(ref.id)} » (${LIST_LABEL[ref.list]}) → conséquence supprimée`,
      { type: "decision", id: ref.id }
    );
  }

  const page = clonePage(draft, base, ref.id);
  const list = [...(page.page_general_consequence || [])];
  const [removed] = list.splice(ref.index, 1);
  if (!removed) return draft;
  page.page_general_consequence = list;
  return withChange(
    draft,
    { pages: { ...draft.pages, [ref.id]: page } },
    `Page « ${base.pageLabel(ref.id)} » → conséquence supprimée`,
    { type: "page", id: ref.id }
  );
}

/**
 * Modifie une conséquence existante (condition et/ou page cible).
 * patch : { condition, condition_metadata, previous_decision_id, consequence_id }
 *
 * Cas important : si cette conséquence est pilotée pour l'atelier courant par
 * `variation_game_config[configId]`, on écrit la nouvelle cible DANS la
 * variante — sinon on modifierait silencieusement tous les autres ateliers.
 */
export function updateConsequence(draft, base, ref, patch, configId) {
  const applyPatch = (consequence) => {
    const next = { ...consequence, ...patch };
    if ("consequence_id" in patch) {
      const variations = consequence.variation_game_config || {};
      if (configId in variations) {
        next.consequence_id = consequence.consequence_id;
        next.variation_game_config = { ...variations, [configId]: patch.consequence_id || null };
      }
    }
    if (!next.condition) {
      delete next.condition_metadata;
      delete next.previous_decision_id;
    }
    return next;
  };

  if (ref.doc === "decision") {
    const decision = cloneDecision(draft, base, ref.id);
    const list = [...(decision[ref.list] || [])];
    if (!list[ref.index]) return draft;
    list[ref.index] = applyPatch(list[ref.index]);
    decision[ref.list] = list;
    return withChange(
      draft,
      { decisions: { ...draft.decisions, [ref.id]: decision } },
      `« ${base.decisionLabel(ref.id)} » (${LIST_LABEL[ref.list]}) → conséquence modifiée`,
      { type: "decision", id: ref.id }
    );
  }

  const page = clonePage(draft, base, ref.id);
  const list = [...(page.page_general_consequence || [])];
  if (!list[ref.index]) return draft;
  list[ref.index] = applyPatch(list[ref.index]);
  page.page_general_consequence = list;
  return withChange(
    draft,
    { pages: { ...draft.pages, [ref.id]: page } },
    `Page « ${base.pageLabel(ref.id)} » → conséquence modifiée`,
    { type: "page", id: ref.id }
  );
}

/* ─── Création rapide de page ───────────────────────────────────── */

export function createPage(draft, { _id, title, type }) {
  const page = {
    _id,
    title,
    type,
    description: "",
    // Une page créée depuis le graphe naît en « texte seul » : on vient d'y
    // écrire un titre, pas de déposer un visuel, et c'est le format qui rend
    // une page utilisable sans média.
    content: type === "context" ? { layout: "text", text: "" } : {},
  };
  if (["mosaic", "one-choice", "command_control"].includes(type)) page.content.list_decisions = [];
  if (type === "score") page.content.list_graph = [];
  if (type === "two_doughnuts_graphs") page.content.list_scores = [];

  return withChange(
    draft,
    {
      pages: { ...draft.pages, [_id]: page },
      createdPages: [...draft.createdPages, _id],
    },
    `Page « ${title || _id} » créée (${type})`,
    { type: "page", id: _id }
  );
}

/* ─── Sauvegarde ─────────────────────────────────────────────────
   Ordre imposé : pages d'abord (une conséquence ou une pile peut pointer
   vers une page créée dans le brouillon — elle doit exister en base avant
   que le validateur serveur n'évalue ce qui la référence), puis décisions,
   puis la config.

   En cas de refus (`blocked`), on s'arrête et on remonte le détail : le
   composant propose alors de forcer, ce qui rejoue TOUTE la séquence avec
   `_force_save` (les upserts déjà passés sont idempotents).
*/
function normalizeConsequences(list) {
  return (list || []).map((c) => {
    const next = { ...c };
    if (!next.consequence_id) next.consequence_id = null;
    if (next.variation_game_config) {
      const cleaned = {};
      Object.entries(next.variation_game_config).forEach(([k, v]) => { cleaned[k] = v || null; });
      next.variation_game_config = cleaned;
    }
    return next;
  });
}

export async function saveDraft(draft, { apiUrl, token, force = false }) {
  const post = async (path, body) => {
    const res = await fetch(`${apiUrl}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify(force ? { ...body, _force_save: true } : body),
    });
    return res.json();
  };

  const affectedConfigs = new Set();

  for (const page of Object.values(draft.pages)) {
    const payload = {
      ...page,
      page_general_consequence: normalizeConsequences(page.page_general_consequence),
    };
    const result = await post("/save_page", payload);
    if (result.blocked) return { ok: false, blocked: true, entity: "page", id: page._id, result };
    if (result.success === false) return { ok: false, entity: "page", id: page._id, result };
    (result.affected_configs || []).forEach((c) => affectedConfigs.add(c));
  }

  for (const decision of Object.values(draft.decisions)) {
    const payload = {
      ...decision,
      add_elem_to_pile_if_taken: normalizeConsequences(decision.add_elem_to_pile_if_taken),
      add_elem_to_pile_if_not_taken: normalizeConsequences(decision.add_elem_to_pile_if_not_taken),
    };
    const result = await post("/save_decision", payload);
    if (result.blocked) return { ok: false, blocked: true, entity: "decision", id: decision._id, result };
    if (result.success === false) return { ok: false, entity: "decision", id: decision._id, result };
    (result.affected_configs || []).forEach((c) => affectedConfigs.add(c));
  }

  if (draft.config) {
    const result = await post("/save_game_config", draft.config);
    if (result.blocked) return { ok: false, blocked: true, entity: "config", id: draft.config._id, result };
    if (result.success === false) return { ok: false, entity: "config", id: draft.config._id, result };
  }

  return { ok: true, affectedConfigs: Array.from(affectedConfigs) };
}
